import axios from 'axios';

// Create axios instance
const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear token and redirect to appropriate login page
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      const isMobile = window.location.pathname.startsWith('/mobile/');
      window.location.href = isMobile ? '/mobile/login' : '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (username, password) => {
    const formData = new FormData();
    formData.append('username', username);
    formData.append('password', password);
    return api.post('/auth/login', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  me: () => api.get('/auth/me'),
  requestPasswordReset: (email) => api.post('/auth/password-reset/request', { email }),
  confirmPasswordReset: (token, newPassword) =>
    api.post('/auth/password-reset/confirm', { token, new_password: newPassword }),
};

// Files API
export const filesAPI = {
  upload: (file, onProgress, languageCode = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (languageCode) {
      formData.append('language_code', languageCode);
    }
    return api.post('/files/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 0, // No timeout for large file uploads
      onUploadProgress: (progressEvent) => {
        if (onProgress) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percent);
        }
      },
    });
  },
  list: () => api.get('/files'),
  download: (filename) => api.get(`/files/${filename}`, { responseType: 'blob' }),
  delete: (filename) => api.delete(`/files/${filename}`),
};

// Transcripts API
export const transcriptsAPI = {
  getStatus: (id) => api.get(`/transcripts/${id}/status`),
  getStatusByTask: (taskId) => api.get(`/transcripts/by-task/${taskId}/status`),
  get: (id) => api.get(`/transcripts/${id}`),
  download: (id, format = 'txt') =>
    api.get(`/transcripts/${id}/download?format=${format}`, { responseType: 'blob' }),
};

// Admin API
export const adminAPI = {
  getUsers: () => api.get('/admin/users'),
  createUser: (userData) => api.post('/admin/users', userData),
  updateUser: (username, userData) => api.put(`/admin/users/${username}`, userData),
  deleteUser: (username) => api.delete(`/admin/users/${username}`),
  purgeUserContent: (username) => api.delete(`/admin/users/${username}/content`),
  getStats: () => api.get('/admin/stats'),
};

export default api;
